/**
 * Change password for a signed-in user.
 *
 * Calls Cognito's ChangePassword API directly with the stored access token
 * rather than going through the amazon-cognito-identity-js session, because the
 * app keeps its own tokens in localStorage and the SDK session can be cleared
 * (api.ts purges tokens that were issued by a different user pool).
 *
 * No email is involved: the current password proves ownership, so this works
 * even though Cognito cannot deliver recovery mail to this tenant.
 */

const REGION = process.env.REACT_APP_COGNITO_REGION || 'eu-west-2';
const ENDPOINT = `https://cognito-idp.${REGION}.amazonaws.com/`;

const MESSAGES: Record<string, string> = {
  NotAuthorizedException: 'Your current password is not correct.',
  InvalidPasswordException: 'The new password does not meet the password policy (8+ characters with upper, lower, number and symbol).',
  LimitExceededException: 'Too many attempts. Please wait a moment and try again.',
  PasswordHistoryPolicyViolationException: 'Choose a password you have not used recently.',
  UserNotFoundException: 'That account could not be found.',
};

export const changePassword = async (previousPassword: string, proposedPassword: string): Promise<void> => {
  const accessToken = localStorage.getItem('accessToken');
  if (!accessToken) {
    throw new Error('Your session has expired. Please sign in again.');
  }

  const res = await fetch(ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AWSCognitoIdentityProviderService.ChangePassword',
    },
    body: JSON.stringify({
      AccessToken: accessToken,
      PreviousPassword: previousPassword,
      ProposedPassword: proposedPassword,
    }),
  });

  if (!res.ok) {
    const data: any = await res.json().catch(() => ({}));
    const type = String(data?.__type || '').split('#').pop() || '';
    throw new Error(MESSAGES[type] || data?.message || 'Could not change the password.');
  }
};
